import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { icolor_to_col4, vector_3 } from "../../../../babylon/utility/Conversion.js";
import { DebugVisualizer } from "../../../../babylon/debug/visualizers/DebugVisualizer.js";
import { IColor, IVector3, LineType } from "../../../../api.js";
import { EPSILON_ANGLE_RADIANS } from "../../../../Math.js";
const COLORS = {
    GREEN: new IColor(0, 1, 0),
    BLUE: new IColor(0, 0, 1),
    RED: new IColor(1, 0, 0)
};
export class OrbitPlannerCameraVisualizer extends DebugVisualizer {
    constructor(_camera, viewer) {
        super(viewer);
        this._camera = _camera;
        this.show_collision_radius = true;
        this.show_target = true;
        this.show_pan_limits = true;
        this.show_orbit_limits = true;
        this.update();
    }
    update() {
        // Remove all existing lines
        this.remove_lines(0);
        let index = 0;
        const is_active = this._camera.viewer.get_active_camera() === this._camera;
        index = this.draw_camera_position(index);
        index = this.draw_view_frame_rectangle(index);
        if (this.show_collision_radius && !is_active) {
            index = this.draw_collision_radius(index);
        }
        if (this.show_target) {
            index = this.draw_target(index);
        }
        if (this.show_pan_limits) {
            index = this.draw_pan_limits(index);
        }
        if (this.show_orbit_limits) {
            this.draw_orbit_limits(index);
        }
    }
    draw_camera_position(index) {
        const color = icolor_to_col4(COLORS.GREEN);
        const thickness = 0.02;
        const camera_pos = vector_3(this._camera.position);
        // Draw a small cross at the camera position
        const size = 0.1;
        const up = BABYLON_Vector3.Up().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(up), camera_pos.add(up), color, thickness);
        const right = BABYLON_Vector3.Right().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(right), camera_pos.add(right), color, thickness);
        const forward = BABYLON_Vector3.Forward().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(forward), camera_pos.add(forward), color, thickness);
        return index;
    }
    draw_view_frame_rectangle(index) {
        const color = icolor_to_col4(COLORS.GREEN);
        const thickness = 0.02;
        const camera_pos = vector_3(this._camera.position);
        const forward = vector_3(this._camera.forward).normalize();
        const up = vector_3(this._camera.up).normalize();
        const right = vector_3(this._camera.right).normalize();
        const left = vector_3(this._camera.left).normalize();
        // Totaly reasonable distance to draw the view frame
        const distance = 0.75;
        // Calculate the height and width of the view frame
        let fov = this._camera.field_of_view_radians;
        fov = 2 * Math.atan(Math.tan(fov / 2) / this._camera.projection.aspect_ratio);
        const aspect = this._camera.projection.aspect_ratio;
        const height = 2 * distance * Math.tan(fov / 2);
        const width = height * aspect;
        // The center of the view frame rectangle
        const center = camera_pos.add(forward.scale(distance));
        // Four corners of the rectangle
        const top_left = center.add(up.scale(height / 2)).subtract(right.scale(width / 2));
        const top_right = center.add(up.scale(height / 2)).add(right.scale(width / 2));
        const bottom_left = center.subtract(up.scale(height / 2)).subtract(right.scale(width / 2));
        const bottom_right = center.subtract(up.scale(height / 2)).add(right.scale(width / 2));
        // Draw view frame rectangle
        this.draw_line(index++, top_left, top_right, color, thickness);
        this.draw_line(index++, top_right, bottom_right, color, thickness);
        this.draw_line(index++, bottom_right, bottom_left, color, thickness);
        this.draw_line(index++, bottom_left, top_left, color, thickness);
        // Draw connection lines
        this.draw_line(index++, camera_pos, top_left, color, thickness);
        this.draw_line(index++, camera_pos, top_right, color, thickness);
        this.draw_line(index++, camera_pos, bottom_left, color, thickness);
        this.draw_line(index++, camera_pos, bottom_right, color, thickness);
        // Draw Up Vector
        const up_vector_thickness = 0.01;
        const arrow_base_center = center.add(up.scale(height / 2)).add(up.scale(0.05));
        const arrow_length = 0.15;
        const arrow_width = IVector3.distance(top_left, top_right);
        const arrow_left = arrow_base_center.add(left.scale(arrow_width / 2));
        const arrow_right = arrow_base_center.add(right.scale(arrow_width / 2));
        const arrow_top = arrow_base_center.add(up.scale(arrow_length));
        // Draw the up vector triangle
        this.draw_line(index++, arrow_left, arrow_top, color, up_vector_thickness);
        this.draw_line(index++, arrow_top, arrow_right, color, up_vector_thickness);
        this.draw_line(index++, arrow_right, arrow_left, color, up_vector_thickness);
        return index;
    }
    draw_collision_radius(index) {
        const color = icolor_to_col4(COLORS.GREEN);
        const thickness = 0.01;
        const cam_pos = vector_3(this._camera.position);
        const radius = this._camera.get_ground_collision_radius();
        const rotation_axes = [BABYLON_Vector3.Up(), BABYLON_Vector3.Right(), BABYLON_Vector3.Forward()];
        rotation_axes.forEach(axis => {
            let perpendicular;
            if (Math.abs(BABYLON_Vector3.Dot(axis, BABYLON_Vector3.Up())) < 0.9) {
                perpendicular = BABYLON_Vector3.Cross(axis, BABYLON_Vector3.Up()).normalize();
            }
            else {
                perpendicular = BABYLON_Vector3.Cross(axis, BABYLON_Vector3.Right()).normalize();
            }
            const start_point = perpendicular.scale(radius).add(cam_pos);
            index += this.draw_curve(index, start_point, start_point, axis, cam_pos, Math.PI * 2, color, thickness);
        });
        return index;
    }
    draw_target(index) {
        const dis_color = icolor_to_col4(COLORS.GREEN);
        const dis_thickness = 0.01;
        const cross_color = icolor_to_col4(COLORS.GREEN);
        const cross_thickness = 0.02;
        const camera_pos = vector_3(this._camera.position);
        const up = vector_3(this._camera.up).normalize();
        const right = vector_3(this._camera.right).normalize();
        const target_pos = vector_3(this._camera.target);
        const is_active = this._camera.viewer.get_active_camera() === this._camera;
        if (!is_active) {
            // Draw distance
            this.draw_line(index++, camera_pos, target_pos, dis_color, dis_thickness, LineType.DASHED);
        }
        // Draw target cross
        const size = 0.5;
        const up_vec = up.scale(size / 2);
        this.draw_line(index++, target_pos.subtract(up_vec), target_pos.add(up_vec), cross_color, cross_thickness);
        const right_vec = right.scale(size / 2);
        this.draw_line(index++, target_pos.subtract(right_vec), target_pos.add(right_vec), cross_color, cross_thickness);
        return index;
    }
    draw_pan_limits(index) {
        const color = icolor_to_col4(COLORS.BLUE);
        const thickness = 0.005;
        const max = this._camera['_pan_max'];
        const min = this._camera['_pan_min'];
        // Do nothing if we have infinitly large pan limits, ex. unset
        if (!is_finite_vector(max) || !is_finite_vector(min))
            return index;
        const v1 = { x: min.x, y: min.y, z: min.z };
        const v2 = { x: max.x, y: min.y, z: min.z };
        const v3 = { x: max.x, y: max.y, z: min.z };
        const v4 = { x: min.x, y: max.y, z: min.z };
        const v5 = { x: min.x, y: min.y, z: max.z };
        const v6 = { x: max.x, y: min.y, z: max.z };
        const v7 = { x: max.x, y: max.y, z: max.z };
        const v8 = { x: min.x, y: max.y, z: max.z };
        const draw_line = (a, b) => {
            this.draw_line(index++, vector_3(a), vector_3(b), color, thickness, LineType.DASHED);
        };
        // Draw the bottom square (v1, v2, v3, v4)
        draw_line(v1, v2);
        draw_line(v2, v3);
        draw_line(v3, v4);
        draw_line(v4, v1);
        // Draw the top square (v5, v6, v7, v8)
        draw_line(v5, v6);
        draw_line(v6, v7);
        draw_line(v7, v8);
        draw_line(v8, v5);
        // Draw the vertical edges
        draw_line(v1, v5);
        draw_line(v2, v6);
        draw_line(v3, v7);
        draw_line(v4, v8);
        return index;
    }
    draw_orbit_limits(index) {
        const yaw_min = this._camera['_yaw_min'];
        const yaw_max = this._camera['_yaw_max'];
        const pitch_min = this._camera['_pitch_min'];
        const pitch_max = this._camera['_pitch_max'];
        const color = icolor_to_col4(COLORS.BLUE);
        const thickness = 0.005;
        // If yaw or pitch limits are infinite, do nothing
        if (!isFinite(yaw_min) || !isFinite(yaw_max) || !isFinite(pitch_min) || !isFinite(pitch_max)) {
            return index;
        }
        if (yaw_max - yaw_min < EPSILON_ANGLE_RADIANS || pitch_max - pitch_min < EPSILON_ANGLE_RADIANS) {
            return index;
        }
        const target_pos = vector_3(this._camera.target);
        const camera_pos = vector_3(this._camera.position);
        const radius = BABYLON_Vector3.Distance(camera_pos, target_pos);
        if (this._camera.viewer.get_active_camera() !== this._camera) {
            const num_segments_yaw = 12;
            const num_segments_pitch = 6;
            // Draw lines of pitch
            for (let j = 0; j <= num_segments_pitch; j++) {
                const pitch = pitch_min + (pitch_max - pitch_min) * j / num_segments_pitch;
                for (let i = 0; i < num_segments_yaw; i++) {
                    const yaw1 = yaw_min + (yaw_max - yaw_min) * i / num_segments_yaw;
                    const yaw2 = yaw_min + (yaw_max - yaw_min) * (i + 1) / num_segments_yaw;
                    const point1 = this.spherical_to_cartesian(radius, yaw1, pitch).add(target_pos);
                    const point2 = this.spherical_to_cartesian(radius, yaw2, pitch).add(target_pos);
                    this.draw_line(index++, point1, point2, color, thickness, LineType.DASHED);
                }
            }
            // Draw lines of yaw
            for (let i = 0; i <= num_segments_yaw; i++) {
                const yaw = yaw_min + (yaw_max - yaw_min) * i / num_segments_yaw;
                for (let j = 0; j < num_segments_pitch; j++) {
                    const pitch1 = pitch_min + (pitch_max - pitch_min) * j / num_segments_pitch;
                    const pitch2 = pitch_min + (pitch_max - pitch_min) * (j + 1) / num_segments_pitch;
                    const point1 = this.spherical_to_cartesian(radius, yaw, pitch1).add(target_pos);
                    const point2 = this.spherical_to_cartesian(radius, yaw, pitch2).add(target_pos);
                    this.draw_line(index++, point1, point2, color, thickness, LineType.DASHED);
                }
            }
        }
        // Corner points
        const corners = [
            { yaw: yaw_min, pitch: pitch_min }, // Bottom left
            { yaw: yaw_max, pitch: pitch_min }, // Bottom right
            { yaw: yaw_min, pitch: pitch_max }, // Top left
            { yaw: yaw_max, pitch: pitch_max } // Top right
        ];
        // Draw lines from corner points to camera position
        for (const corner of corners) {
            const corner_point = this.spherical_to_cartesian(radius, corner.yaw, corner.pitch).add(target_pos);
            this.draw_line(index++, corner_point, target_pos, color, thickness, LineType.DASHED);
        }
        return index;
    }
    spherical_to_cartesian(radius, yaw, pitch) {
        const x = radius * Math.cos(-pitch) * Math.sin(-yaw);
        const y = radius * Math.sin(-pitch);
        const z = radius * Math.cos(-pitch) * Math.cos(-yaw);
        return new BABYLON_Vector3(x, y, z);
    }
}
function is_finite(...values) {
    return values.every(v => isFinite(v));
}
function is_finite_vector({ x, y, z }) {
    return is_finite(x, y, z);
}
//# sourceMappingURL=OrbitPlannerCameraVisualizer.js.map